// Korrigierte Version von AchievementSuggestions.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Achievement } from '../../services/achievementService';
import AchievementCard from './AchievementCard';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

interface SuggestionCategory {
  id: string;
  name: string;
  achievements: Achievement[];
}

interface AchievementSuggestionsProps {
  categories: SuggestionCategory[];
  userIsAuthenticated: boolean;
}

const AchievementSuggestions: React.FC<AchievementSuggestionsProps> = ({
  categories,
  userIsAuthenticated
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // Erhöht auf 9 Vorschläge (3 pro Reihe in der Grid-Ansicht)
  const maxSuggestionsPerCategory = 9;
  
  // Ensure categories is an array and filter out completed achievements
  // Also improve the sorting based on progress percentage
  const validCategories = Array.isArray(categories) ? categories.map((category: SuggestionCategory) => {
    if (!Array.isArray(category.achievements)) {
      return { ...category, achievements: [] };
    }
    
    // Filter out completed achievements
    let filteredAchievements = category.achievements.filter(
      achievement => !achievement.is_completed
    );
    
    // Sortiere nach dem genauen Prozentsatz und dann nach Punkten
    filteredAchievements = [...filteredAchievements].sort((a, b) => {
      // Verwende exact_percentage für präzise Sortierung, wenn verfügbar
      const aProgress = a.progress?.exact_percentage !== undefined 
        ? a.progress.exact_percentage 
        : a.progress?.percentage || 0;
      
      const bProgress = b.progress?.exact_percentage !== undefined 
        ? b.progress.exact_percentage 
        : b.progress?.percentage || 0;
      
      // Vergleiche mit kleiner Toleranz für Fließkommazahlen
      if (Math.abs(aProgress - bProgress) > 0.001) {
        return bProgress - aProgress; // Absteigend sortieren
      }
      
      // Bei gleichem Fortschritt nach Punkten sortieren
      return (b.points || 0) - (a.points || 0);
    });
    
    return { ...category, achievements: filteredAchievements };
  }) : [];
  
  // For non-empty categories check (we display all categories, but only return null if we have none)
  const hasAtLeastOneAchievement = validCategories.some(
    cat => Array.isArray(cat.achievements) && cat.achievements.length > 0
  );
  
  // Find a category with achievements if possible, otherwise use the first one
  const defaultCategoryId = validCategories.length > 0 ? 
    (validCategories.find(cat => cat.achievements.length > 0)?.id || validCategories[0].id) :
    '';
  
  const [activeCategory, setActiveCategory] = useState<string>(defaultCategoryId);
  const [visibleCount, setVisibleCount] = useState<number>(maxSuggestionsPerCategory);

  // Update visible count based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        // Auf kleinen Bildschirmen nur 3 anzeigen
        setVisibleCount(3);
      } else if (window.innerWidth < 1024) {
        // Auf mittleren Bildschirmen 6 anzeigen (2 Reihen mit je 3)
        setVisibleCount(6);
      } else {
        // Auf großen Bildschirmen alle 9 anzeigen (3 Reihen mit je 3)
        setVisibleCount(maxSuggestionsPerCategory);
      }
    };

    handleResize(); // Initial call
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Early return if no categories at all
  if (validCategories.length === 0) {
    return null;
  }
  
  // Early return if no achievements in any category and user is not authenticated
  // (we only show empty categories to authenticated users)
  if (!hasAtLeastOneAchievement && !userIsAuthenticated) {
    return null;
  }

  // Find active category safely
  const currentCategory = validCategories.find(cat => cat.id === activeCategory) || validCategories[0];
  
  // Check if active category has achievements
  const hasAchievements = currentCategory && 
                         Array.isArray(currentCategory.achievements) && 
                         currentCategory.achievements.length > 0;

  // Handle navigation to category
  const goToCategory = (categoryId: string) => {
    const category = validCategories.find(cat => cat.id === categoryId);
    if (category && Array.isArray(category.achievements) && category.achievements.length > 0) {
      // Navigate to the first achievement in this category
      navigate(`/achievements/${category.achievements[0].id}`);
    }
  };

  // Get translated category name using the translation system
  const getCategoryName = (categoryId: string): string => {
    // Use the name from the category if it's available - server might send translated names
    const category = validCategories.find(cat => cat.id === categoryId);
    if (category && category.name) {
      return category.name;
    }
    
    // Translate via the t() function from the LanguageContext
    switch (categoryId) {
      case 'raid':
        return t('achievements.raids');
      case 'dungeon':
        return t('achievements.dungeons');
      case 'strike':
        return t('achievements.strikes');
      case 'crucible':
        return t('achievements.crucible');
      case 'gambit':
        return t('achievements.gambit');
      case 'world':
        return t('achievements.destinations');
      case 'seasonal':
        return t('achievements.seasonal');
      case 'exotic':
        return t('achievements.exotics');
      case 'campaign':
        return t('achievements.campaign');
      case 'lostsector':
        return t('achievements.lostsectors');
      case 'other':
        return t('achievements.other');
      default:
        // Use the id as a last resort but attempt to translate it
        return t(`achievements.${categoryId}`, categoryId);
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6 mb-6">
      <h2 className="text-xl font-bold text-destiny-gold mb-4">
        {t('achievements.suggestions')}
      </h2>
      
      {/* Categories selector */}
      <div className="flex flex-wrap items-center space-x-2 mb-6 overflow-x-auto pb-2">
        {validCategories.map(category => {
          // Zeige den Counter basierend auf der tatsächlichen Anzahl verfügbarer Achievements,
          // aber begrenzt durch visibleCount
          const displayCount = Math.min(category.achievements.length, visibleCount);
          
          return (
            <button
              key={category.id}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeCategory === category.id
                  ? 'bg-destiny-blue text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              onClick={() => setActiveCategory(category.id)}
            >
              {/* Get the translated category name */}
              {getCategoryName(category.id)}
              {displayCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold rounded-full bg-gray-800 text-gray-200">
                  {displayCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Achievement Cards Carousel */}
      <div className="relative">
        {/* Achievements display */}
        {hasAchievements ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.isArray(currentCategory.achievements) && 
              currentCategory.achievements.slice(0, visibleCount).map(achievement => (
                <AchievementCard
                  key={achievement.id}
                  achievement={achievement}
                  showProgress={userIsAuthenticated}
                />
              ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-400">
            {t('achievements.noSuggestionsInCategory')}
          </div>
        )}

        {/* Navigation buttons */}
        {hasAchievements && currentCategory.achievements.length > visibleCount && (
          <div className="flex justify-between mt-4">
            <button
              onClick={() => goToCategory(activeCategory)}
              className="text-destiny-blue hover:text-destiny-gold flex items-center font-medium"
            >
              {t('achievements.viewAllInCategory')}
              <ChevronRightIcon className="h-4 w-4 ml-1" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AchievementSuggestions;